import React, { ComponentProps, useCallback, useMemo } from 'react';
import { useArrayPagination } from '../../hooks/useArrayPagination';
import {
  FilterPipelineFilter,
  useFilterPipeline
} from '../../hooks/useFilterPipeline/useFilterPipeline';
import { CreateEntityButton } from '../../misc/CreateEntityButton/CreateEntityButton';
import { FilterBar } from '../../misc/FilterBar/FilterBar';
import { Pagination } from '../../misc/Pagination/Pagination';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import { PageContentContainer } from '../PageContentContainer/PageContentContainer';
import {
  IconConfig,
  PageContentHeader,
  PageContentHeaderProps
} from '../PageContentHeader/PageContentHeader';
import styles from './ManageEntitiesContentLayout.module.css';

export type ManageEntitiesContentLayoutProps<TEntity, TCreateEntityButtonItem> =
  PropsWithoutChildren<ComponentProps<'div'>> & {
    headerConfig: HeaderConfig;
    entities: Array<TEntity>;
    renderEntities: (currentPageEntities: Array<TEntity>) => JSX.Element;
    createEntityButtonConfig: CreateEntityButtonConfig<TCreateEntityButtonItem>;
    /**
     * default: true
     */
    usePagination?: boolean;
    /**
     * default: []
     */
    filters?: Array<FilterPipelineFilter<TEntity, any>>;
  };

export type HeaderConfig = {
  iconConfig: IconConfig;
  title: string;
  /* gets placed left of the create entity button */
  renderAdditionalRightContent?: () => Array<JSX.Element>;
};

export type CreateEntityButtonConfig<TCreateEntityButtonItem> =
  | { visible: false }
  | {
      visible: true;
      text: string;
      dropdownItems: Array<TCreateEntityButtonItem>;
      getDropdownItemDisplayText: (item: TCreateEntityButtonItem) => string;
      getDropdownItemKey: (item: TCreateEntityButtonItem) => string;
      onCreateEntity: (item: TCreateEntityButtonItem | null) => void;
    };

export const ManageEntitiesContentLayout = React.memo(
  <TEntity, TCreateEntityButtonItem>(
    props: ManageEntitiesContentLayoutProps<TEntity, TCreateEntityButtonItem>
  ) => {
    const {
      entities,
      createEntityButtonConfig,
      renderEntities,
      headerConfig,
      usePagination = true,
      filters = [],
      ...remainingProps
    } = prepareProps({ props, ignoreChildren: true });

    const { filteredItems: filteredEntities, handle: filterPipelineHandle } =
      useFilterPipeline({ items: entities, filters });
    const { paginationHandle, currentPageItems } = useArrayPagination(
      filteredEntities,
      10
    );
    const entitiesToRender = useMemo(
      () => (usePagination ? currentPageItems : filteredEntities),
      [usePagination, currentPageItems, filteredEntities]
    );

    const renderedEntities = useMemo<JSX.Element>(
      () => renderEntities(entitiesToRender),
      [renderEntities, entitiesToRender]
    );

    const renderRightContentElements: PageContentHeaderProps['renderRightContentElements'] =
      useCallback(() => {
        const additionalElements =
          headerConfig.renderAdditionalRightContent?.();
        const elements: Array<JSX.Element> = additionalElements
          ? [...additionalElements]
          : [];

        if (createEntityButtonConfig.visible) {
          elements.push(
            <CreateEntityButton<TCreateEntityButtonItem>
              text={createEntityButtonConfig.text}
              key="ManageEntitiesContentLayout.CreateEntityButton"
              dropdownItems={createEntityButtonConfig.dropdownItems}
              getDropdownItemDisplayText={
                createEntityButtonConfig.getDropdownItemDisplayText
              }
              getDropdownItemKey={createEntityButtonConfig.getDropdownItemKey}
              onCreateEntity={createEntityButtonConfig.onCreateEntity}
              data-testid="ManageEntitiesContentLayout.CreateEntityButton"
            ></CreateEntityButton>
          );
        }

        return elements;
      }, [headerConfig, createEntityButtonConfig]);

    const renderFilterBar = useCallback(() => {
      if (!filters.length) {
        return null;
      }

      return (
        <FilterBar
          handle={filterPipelineHandle}
          data-testid="ManageEntitiesContentLayout.FilterBar"
        ></FilterBar>
      );
    }, [filterPipelineHandle, filters]);

    return (
      <PageContentContainer {...remainingProps}>
        <PageContentHeader
          iconConfig={headerConfig.iconConfig}
          title={headerConfig.title}
          renderRightContentElements={renderRightContentElements}
          renderAdditionalBottomContent={renderFilterBar}
        ></PageContentHeader>
        {renderedEntities}
        {usePagination ? (
          <Pagination
            paginationHandle={paginationHandle}
            className={styles.pagination}
            data-testid="ManageEntitiesContentLayout.Pagination"
          ></Pagination>
        ) : null}
      </PageContentContainer>
    );
  }
) as <TEntity, TCreateEntityButtonItem>(
  props: ManageEntitiesContentLayoutProps<TEntity, TCreateEntityButtonItem>
) => JSX.Element;
